/**
 * TYPING-PROCESSOR-LIST-001 — resilient assignee roster loader.
 * status: idle | loading | ready | error. A failed refresh keeps the last good list.
 * `ensureFresh()` is wired to dropdown open: loads when idle/error, or when stale.
 */
import { useState, useRef, useCallback, useEffect } from 'react';

export const ASSIGNEE_LIST_STALE_MS = 5 * 60 * 1000;

export function useAssigneeList(fetcher, enabled) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const loadedAtRef = useRef(0);
  const seqRef = useRef(0);
  const statusRef = useRef('idle');
  statusRef.current = status;

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    setStatus('loading');
    setError(null);
    try {
      const list = await fetcher();
      if (seq !== seqRef.current) return;
      setItems(Array.isArray(list) ? list : []);
      loadedAtRef.current = Date.now();
      setStatus('ready');
    } catch (e) {
      if (seq !== seqRef.current) return;
      setError(e);
      setStatus('error');
    }
  }, [fetcher]);

  const ensureFresh = useCallback(() => {
    const s = statusRef.current;
    if (s === 'loading') return;
    if (s !== 'ready' || Date.now() - loadedAtRef.current > ASSIGNEE_LIST_STALE_MS) load();
  }, [load]);

  useEffect(() => {
    if (enabled) ensureFresh();
  }, [enabled, ensureFresh]);

  return { items, status, error, load, ensureFresh };
}

export default useAssigneeList;
